/**
 * Pricing Engine for SNOWBLOWR
 * Calculates pricing based on city weather data, driveway size, and historical patterns
 */

import { canadianCities } from '@/data/cities';

// Historical weather data for cities (average storms per season)
export const cityWeatherData: Record<string, {
  avgStormsPerSeason: number;
  avgSnowDepthPerStorm: number; // cm
  seasonStart: string;
  seasonEnd: string;
  priceMultiplier: number; // based on cost of living and demand
}> = {
  'ottawa': {
    avgStormsPerSeason: 12,
    avgSnowDepthPerStorm: 18.5,
    seasonStart: 'November 15',
    seasonEnd: 'April 1',
    priceMultiplier: 1.0
  },
  'montreal': {
    avgStormsPerSeason: 13,
    avgSnowDepthPerStorm: 16.2,
    seasonStart: 'November 10',
    seasonEnd: 'April 5',
    priceMultiplier: 1.05
  },
  'toronto': {
    avgStormsPerSeason: 8,
    avgSnowDepthPerStorm: 13.5,
    seasonStart: 'November 20',
    seasonEnd: 'March 25',
    priceMultiplier: 1.15
  },
  'mississauga': {
    avgStormsPerSeason: 8,
    avgSnowDepthPerStorm: 13.5,
    seasonStart: 'November 20',
    seasonEnd: 'March 25',
    priceMultiplier: 1.12
  },
  'brampton': {
    avgStormsPerSeason: 8,
    avgSnowDepthPerStorm: 13.5,
    seasonStart: 'November 20',
    seasonEnd: 'March 25',
    priceMultiplier: 1.10
  },
  'hamilton': {
    avgStormsPerSeason: 10,
    avgSnowDepthPerStorm: 15.0,
    seasonStart: 'November 15',
    seasonEnd: 'March 30',
    priceMultiplier: 0.95
  },
  'london': {
    avgStormsPerSeason: 11,
    avgSnowDepthPerStorm: 17.0,
    seasonStart: 'November 10',
    seasonEnd: 'April 1',
    priceMultiplier: 0.90
  },
  'kitchener': {
    avgStormsPerSeason: 10,
    avgSnowDepthPerStorm: 16.0,
    seasonStart: 'November 15',
    seasonEnd: 'March 30',
    priceMultiplier: 0.92
  },
  'windsor': {
    avgStormsPerSeason: 9,
    avgSnowDepthPerStorm: 14.0,
    seasonStart: 'November 25',
    seasonEnd: 'March 20',
    priceMultiplier: 0.88
  },
  'quebec-city': {
    avgStormsPerSeason: 14,
    avgSnowDepthPerStorm: 22.0,
    seasonStart: 'November 1',
    seasonEnd: 'April 15',
    priceMultiplier: 0.98
  },
  'gatineau': {
    avgStormsPerSeason: 12,
    avgSnowDepthPerStorm: 18.5,
    seasonStart: 'November 15',
    seasonEnd: 'April 1',
    priceMultiplier: 0.95
  },
  'laval': {
    avgStormsPerSeason: 13,
    avgSnowDepthPerStorm: 16.2,
    seasonStart: 'November 10',
    seasonEnd: 'April 5',
    priceMultiplier: 1.03
  },
  'calgary': {
    avgStormsPerSeason: 15,
    avgSnowDepthPerStorm: 12.0,
    seasonStart: 'October 15',
    seasonEnd: 'April 20',
    priceMultiplier: 1.20
  },
  'edmonton': {
    avgStormsPerSeason: 16,
    avgSnowDepthPerStorm: 14.0,
    seasonStart: 'October 10',
    seasonEnd: 'April 25',
    priceMultiplier: 1.15
  },
  'winnipeg': {
    avgStormsPerSeason: 18,
    avgSnowDepthPerStorm: 16.0,
    seasonStart: 'October 5',
    seasonEnd: 'April 30',
    priceMultiplier: 0.85
  },
  'saskatoon': {
    avgStormsPerSeason: 17,
    avgSnowDepthPerStorm: 15.0,
    seasonStart: 'October 10',
    seasonEnd: 'April 25',
    priceMultiplier: 0.80
  },
  'regina': {
    avgStormsPerSeason: 16,
    avgSnowDepthPerStorm: 14.5,
    seasonStart: 'October 15',
    seasonEnd: 'April 20',
    priceMultiplier: 0.80
  },
  'vancouver': {
    avgStormsPerSeason: 3,
    avgSnowDepthPerStorm: 8.0,
    seasonStart: 'December 15',
    seasonEnd: 'February 28',
    priceMultiplier: 1.35
  },
  'surrey': {
    avgStormsPerSeason: 3,
    avgSnowDepthPerStorm: 8.0,
    seasonStart: 'December 15',
    seasonEnd: 'February 28',
    priceMultiplier: 1.30
  }
};

// Base pricing for driveway sizes
export const basePricing = {
  small: 35,   // 1 car
  medium: 50,  // 2 cars
  large: 70    // 3+ cars
};

// Time window multipliers
export const timeWindowMultipliers = {
  asap: 1.3,      // Within 2-4 hours (premium/priority)
  same_day: 1.1,  // Same day, non-emergent
  flexible: 1.0   // Up to 24 hours
};

// Snow depth multipliers
export const depthMultipliers = [
  { maxCm: 5, multiplier: 1.0 },
  { maxCm: 10, multiplier: 1.15 },
  { maxCm: 20, multiplier: 1.35 },
  { maxCm: 30, multiplier: 1.6 },
  { maxCm: Infinity, multiplier: 2.0 }
];

export interface PricingInput {
  citySlug: string;
  drivewaySize: 'small' | 'medium' | 'large';
  snowDepthCm: number;
  timeWindow: 'asap' | 'same_day' | 'flexible';
  addons?: string[];
}

export interface PricingResult {
  customerPrice: number;
  breakdown: {
    base: number;
    depthFactor: number;
    timeFactor: number;
    cityMultiplier: number;
    addonsTotal: number;
  };
  estimatedDuration: number; // minutes
}

export interface StormPassPricing {
  citySlug: string;
  cityName: string;
  seasonPrice: number;
  avgClearsPerSeason: string;
  avgPerClearCost: number;
  payPerJobSeasonTotal: number;
  savings: number;
  savingsPercent: number;
}

/**
 * Calculate snow depth multiplier
 */
function getDepthMultiplier(depthCm: number): number {
  for (const { maxCm, multiplier } of depthMultipliers) {
    if (depthCm <= maxCm) return multiplier;
  }
  return depthMultipliers[depthMultipliers.length - 1].multiplier;
}

/**
 * Calculate pay-per-job pricing
 */
export function calculatePrice(input: PricingInput): PricingResult {
  const city = cityWeatherData[input.citySlug];
  if (!city) {
    throw new Error(`City ${input.citySlug} not found in weather data`);
  }

  // Base rate by driveway size
  const base = basePricing[input.drivewaySize];

  // Depth multiplier
  const depthFactor = getDepthMultiplier(input.snowDepthCm);

  // Time window multiplier
  const timeFactor = timeWindowMultipliers[input.timeWindow];

  // City cost multiplier
  const cityMultiplier = city.priceMultiplier;

  // Calculate raw price
  const raw = base * depthFactor * timeFactor * cityMultiplier;

  // Add-ons (if any)
  const addonRates: Record<string, number> = {
    walkway: 8,
    steps: 5,
    sidewalk: 10,
    salting: 12,
    windrow: 15
  };
  const addonsTotal = (input.addons || []).reduce(
    (sum, addon) => sum + (addonRates[addon] || 0),
    0
  );

  // Final price (rounded to nearest dollar)
  const customerPrice = Math.round(raw + addonsTotal);

  // Estimate duration based on size and depth
  const baseDuration = input.drivewaySize === 'small' ? 20 : input.drivewaySize === 'medium' ? 30 : 45;
  const durationMultiplier = input.snowDepthCm > 20 ? 1.5 : input.snowDepthCm > 10 ? 1.2 : 1.0;
  const estimatedDuration = Math.round(baseDuration * durationMultiplier);

  return {
    customerPrice,
    breakdown: {
      base,
      depthFactor,
      timeFactor,
      cityMultiplier,
      addonsTotal
    },
    estimatedDuration
  };
}

/**
 * Calculate Storm Pass pricing for a city
 */
export function calculateStormPassPricing(citySlug: string, drivewaySize: 'small' | 'medium' | 'large' = 'medium'): StormPassPricing {
  const cityData = canadianCities.find(c => c.slug === citySlug);
  const weatherData = cityWeatherData[citySlug];

  if (!cityData || !weatherData) {
    throw new Error(`City ${citySlug} not found`);
  }

  // Calculate average per-storm cost
  const avgPerClearCost = calculatePrice({
    citySlug,
    drivewaySize,
    snowDepthCm: weatherData.avgSnowDepthPerStorm,
    timeWindow: 'flexible'
  }).customerPrice;

  // Total if paying per job
  const avgStormsLow = Math.max(weatherData.avgStormsPerSeason - 2, 1);
  const avgStormsHigh = weatherData.avgStormsPerSeason + 2;
  const payPerJobSeasonTotal = Math.round(avgPerClearCost * weatherData.avgStormsPerSeason);

  // Storm Pass pricing algorithm:
  // Base: 70% of pay-per-job total (30% discount)
  // Adjusted by city multiplier
  // Minimum $149, Maximum $349
  const baseSeasonPrice = payPerJobSeasonTotal * 0.70;
  const adjustedPrice = baseSeasonPrice * weatherData.priceMultiplier;
  const seasonPrice = Math.max(149, Math.min(349, Math.round(adjustedPrice / 10) * 10)); // Round to nearest $10

  const savings = payPerJobSeasonTotal - seasonPrice;
  const savingsPercent = Math.round((savings / payPerJobSeasonTotal) * 100);

  return {
    citySlug,
    cityName: cityData.name,
    seasonPrice,
    avgClearsPerSeason: `${avgStormsLow}-${avgStormsHigh} clears`,
    avgPerClearCost,
    payPerJobSeasonTotal,
    savings,
    savingsPercent
  };
}

/**
 * Calculate Storm Pass pricing for all cities
 */
export function calculateAllStormPassPricing(drivewaySize: 'small' | 'medium' | 'large' = 'medium'): StormPassPricing[] {
  return Object.keys(cityWeatherData).map(citySlug => {
    try {
      return calculateStormPassPricing(citySlug, drivewaySize);
    } catch (e) {
      console.warn(`Skipping ${citySlug}:`, e);
      return null;
    }
  }).filter((p): p is StormPassPricing => p !== null);
}

/**
 * Get city from postal code (Canadian postal codes only)
 * This is a simplified version - in production, use a proper geocoding API
 */
export function getCityFromPostalCode(postalCode: string): string | null {
  const pc = postalCode.replace(/\s/g, '').toUpperCase();
  
  // Canadian postal code format: A1A 1A1
  if (!/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(pc)) {
    return null;
  }

  // FSA (Forward Sortation Area) - first 3 characters
  const fsa = pc.substring(0, 3);

  // Simple mapping based on FSA
  // K = Eastern Ontario (Ottawa, Kingston)
  // M, L = Toronto area
  // N = Southwest Ontario (London, Windsor)
  // P = Northern Ontario
  // H = Montreal area
  // G = Quebec City area
  // J = Western Quebec (Gatineau)
  // T = Alberta (Calgary, Edmonton)
  // R = Manitoba (Winnipeg)
  // S = Saskatchewan
  // V = BC (Vancouver, Victoria)

  const fsaToCity: Record<string, string> = {
    // Ottawa area
    'K1': 'ottawa', 'K2': 'ottawa', 'K4': 'ottawa',
    
    // Toronto area
    'M1': 'toronto', 'M2': 'toronto', 'M3': 'toronto', 'M4': 'toronto',
    'M5': 'toronto', 'M6': 'toronto', 'M7': 'toronto', 'M8': 'toronto', 'M9': 'toronto',
    'L4': 'brampton', 'L5': 'mississauga', 'L6': 'brampton',
    
    // Hamilton
    'L8': 'hamilton', 'L9': 'hamilton',
    
    // Montreal area
    'H1': 'montreal', 'H2': 'montreal', 'H3': 'montreal', 'H4': 'montreal',
    'H7': 'laval', 'H8': 'montreal', 'H9': 'montreal',
    
    // Quebec City
    'G1': 'quebec-city', 'G2': 'quebec-city', 'G3': 'quebec-city',
    
    // Gatineau
    'J8': 'gatineau', 'J9': 'gatineau',
    
    // Calgary
    'T1': 'calgary', 'T2': 'calgary', 'T3': 'calgary',
    
    // Edmonton
    'T5': 'edmonton', 'T6': 'edmonton', 'T8': 'edmonton',
    
    // Winnipeg
    'R2': 'winnipeg', 'R3': 'winnipeg',
    
    // Vancouver
    'V5': 'vancouver', 'V6': 'vancouver', 'V7': 'vancouver'
  };

  return fsaToCity[fsa.substring(0, 2)] || null;
}

